import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Collection {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  cover_image: string | null;
  icon: string | null;
  is_active: boolean;
  display_order: number;
  collection_type: string;
  filter_criteria: {
    tags?: string[];
    is_digital?: boolean;
    has_discount?: boolean;
    categories?: string[];
    keywords?: string[];
    is_print_on_demand?: boolean;
    ships_to_us?: boolean;
    include_product_ids?: string[];
    exclude_product_ids?: string[];
  } | null;
  created_at: string;
  updated_at: string;
}

export interface CollectionProduct {
  id: string;
  title: string;
  price: number;
  images: string[];
  is_digital: boolean;
  seller_id: string;
  discount_price: number | null;
  discount_start_date: string | null;
  discount_end_date: string | null;
}

const fetchCollections = async (): Promise<Collection[]> => {
  const { data, error } = await supabase
    .from("collections")
    .select("*")
    .eq("is_active", true)
    .order("display_order", { ascending: true });

  if (error) {
    console.error("Error fetching collections:", error);
    return [];
  }

  return data as Collection[];
};

const fetchCollectionBySlug = async (slug: string): Promise<Collection | null> => {
  const { data, error } = await supabase
    .from("collections")
    .select("*")
    .eq("slug", slug)
    .eq("is_active", true)
    .maybeSingle();

  if (error) {
    console.error("Error fetching collection:", error);
    return null;
  }

  return data as Collection | null;
};

const fetchCollectionProducts = async (collection: Collection): Promise<CollectionProduct[]> => {
  const criteria = collection.filter_criteria;
  
  // For dynamic collections, build query based on criteria
  if (collection.collection_type === "dynamic" && criteria) {
    let query = supabase
      .from("products")
      .select("id, title, price, images, is_digital, seller_id, discount_price, discount_start_date, discount_end_date, category_id")
      .eq("status", "active");

    // Filter by is_digital
    if (criteria.is_digital !== undefined) {
      query = query.eq("is_digital", criteria.is_digital);
    }

    // Filter by is_print_on_demand
    if (criteria.is_print_on_demand !== undefined) {
      query = query.eq("is_print_on_demand", criteria.is_print_on_demand);
    }

    // Filter by has_discount (products with active discounts)
    if (criteria.has_discount) {
      const now = new Date().toISOString();
      query = query
        .not("discount_price", "is", null)
        .or(`discount_start_date.is.null,discount_start_date.lte.${now}`)
        .or(`discount_end_date.is.null,discount_end_date.gte.${now}`);
    }

    // Filter by ships_to_us: only products with shipping_cost_us defined or POD
    if (criteria.ships_to_us) {
      query = query.or("shipping_cost_us.not.is.null,is_print_on_demand.eq.true");
    }

    // Filter by tags (array overlap)
    if (criteria.tags && criteria.tags.length > 0) {
      query = query.overlaps("tags", criteria.tags);
    }

    // Filter by categories (lookup IDs from slugs)
    if (criteria.categories && criteria.categories.length > 0) {
      const { data: categoryData } = await supabase
        .from("categories")
        .select("id")
        .in("slug", criteria.categories);
      
      if (categoryData && categoryData.length > 0) {
        const categoryIds = categoryData.map((c) => c.id);
        query = query.in("category_id", categoryIds);
      }
    }

    // Filter by keywords in title/description
    if (criteria.keywords && criteria.keywords.length > 0) {
      const keywordFilters = criteria.keywords
        .map((kw) => `title.ilike.%${kw}%,description.ilike.%${kw}%`)
        .join(",");
      query = query.or(keywordFilters);
    }

    const { data, error } = await query.limit(50);

    if (error) {
      console.error("Error fetching collection products:", error);
      return [];
    }

    let allProducts = data || [];

    // Merge explicitly included products
    if (criteria.include_product_ids && criteria.include_product_ids.length > 0) {
      const existingIds = new Set(allProducts.map((p: any) => p.id));
      const missingIds = criteria.include_product_ids.filter(id => !existingIds.has(id));
      if (missingIds.length > 0) {
        const { data: extraProducts } = await supabase
          .from("products")
          .select("id, title, price, images, is_digital, seller_id, discount_price, discount_start_date, discount_end_date, category_id")
          .in("id", missingIds)
          .eq("status", "active");
        if (extraProducts) {
          allProducts = [...allProducts, ...extraProducts];
        }
      }
    }

    // Remove explicitly excluded products
    if (criteria.exclude_product_ids && criteria.exclude_product_ids.length > 0) {
      const excluded = new Set(criteria.exclude_product_ids);
      allProducts = allProducts.filter((p: any) => !excluded.has(p.id));
    }

    // Filter out products from sellers with expired memberships
    const productSellerIds = [...new Set(allProducts.map((p: any) => p.seller_id))];
    const { data: activeSellers } = await supabase
      .from("public_seller_stores")
      .select("user_id")
      .in("user_id", productSellerIds);
    const activeIds = new Set((activeSellers || []).map(s => s.user_id));

    return allProducts.filter((p: any) => activeIds.has(p.seller_id)) as CollectionProduct[];
  }

  // For manual collections, fetch from junction table
  const { data: junctionData, error: junctionError } = await supabase
    .from("collection_products")
    .select("product_id")
    .eq("collection_id", collection.id)
    .order("display_order", { ascending: true });

  if (junctionError || !junctionData || junctionData.length === 0) {
    return [];
  }

  const productIds = junctionData.map((jp) => jp.product_id);

  const { data: productsData, error: productsError } = await supabase
    .from("products")
    .select("id, title, price, images, is_digital, seller_id, discount_price, discount_start_date, discount_end_date")
    .in("id", productIds)
    .eq("status", "active");

  if (productsError) {
    console.error("Error fetching manual collection products:", productsError);
    return [];
  }

  // Filter out expired-membership sellers
  const manualSellerIds = [...new Set((productsData || []).map((p: any) => p.seller_id))];
  const { data: activeManualSellers } = await supabase
    .from("public_seller_stores")
    .select("user_id")
    .in("user_id", manualSellerIds);
  const activeManualIds = new Set((activeManualSellers || []).map(s => s.user_id));

  return (productsData || []).filter((p: any) => activeManualIds.has(p.seller_id)) as CollectionProduct[];
};

export const useCollections = (limit?: number) => {
  return useQuery({
    queryKey: ["collections", limit],
    queryFn: async () => {
      const collections = await fetchCollections();
      return limit ? collections.slice(0, limit) : collections;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useCollection = (slug: string) => {
  return useQuery({
    queryKey: ["collection", slug],
    queryFn: () => fetchCollectionBySlug(slug),
    enabled: !!slug,
    staleTime: 1000 * 60 * 5,
  });
};

export const useCollectionProducts = (collection: Collection | null) => {
  return useQuery({
    queryKey: ["collection-products", collection?.id],
    queryFn: () => (collection ? fetchCollectionProducts(collection) : Promise.resolve([])),
    enabled: !!collection,
    staleTime: 1000 * 60 * 5,
  });
};
